import { OPORD_ANALYTICS_SECTION_TEMPLATE_ID } from 'components/DisasterGPT/constants/opordSectionDefaults';

export type DraftOutlineRole = 'category' | 'section';

export type DraftTemplateOutlineRow = {
  id: string;
  title: string;
  role: DraftOutlineRole;
  /**
   * Catalog id to reuse on save. Null means create a new manual section
   * template.
   */
  sectionTemplateId: string | null;
  children: DraftTemplateOutlineRow[];
};

export type NestedTemplateSectionLink = {
  section_id: string;
  order: number;
  children?: NestedTemplateSectionLink[];
};

export type SectionLibraryItem = {
  id: string;
  name: string;
  type: 'manual' | 'ai' | 'automated';
};

export type TemplateSectionLinkInput = {
  section_id: string;
  order?: number;
  children?: TemplateSectionLinkInput[];
};

let outlineIdSeq = 0;

export function nextOutlineRowId(): string {
  outlineIdSeq += 1;
  return `outline-${outlineIdSeq}`;
}

export function isAnalyticsRow(row: DraftTemplateOutlineRow): boolean {
  return row.sectionTemplateId === OPORD_ANALYTICS_SECTION_TEMPLATE_ID;
}

export function isCategoryRow(row: DraftTemplateOutlineRow): boolean {
  return row.role === 'category';
}

export function isSectionRow(row: DraftTemplateOutlineRow): boolean {
  return row.role === 'section';
}

/** Categories can hold sections. Analytics is a category, so it can too. */
export function canHoldChildren(row: DraftTemplateOutlineRow): boolean {
  return isCategoryRow(row);
}

export function createCategoryRow(
  title = '',
  children: DraftTemplateOutlineRow[] = []
): DraftTemplateOutlineRow {
  return {
    id: nextOutlineRowId(),
    title,
    role: 'category',
    sectionTemplateId: null,
    children,
  };
}

export function createSectionRow(
  title = '',
  children: DraftTemplateOutlineRow[] = []
): DraftTemplateOutlineRow {
  return {
    id: nextOutlineRowId(),
    title,
    role: 'section',
    sectionTemplateId: null,
    children,
  };
}

export function createAnalyticsRow(): DraftTemplateOutlineRow {
  return {
    id: nextOutlineRowId(),
    title: 'Analytics',
    role: 'category',
    sectionTemplateId: OPORD_ANALYTICS_SECTION_TEMPLATE_ID,
    children: [],
  };
}

export function createLibraryRow(
  item: SectionLibraryItem
): DraftTemplateOutlineRow {
  if (item.id === OPORD_ANALYTICS_SECTION_TEMPLATE_ID) {
    return createAnalyticsRow();
  }
  return {
    id: nextOutlineRowId(),
    title: item.name,
    role: 'section',
    sectionTemplateId: item.id,
    children: [],
  };
}

export function createEmptyOutline(): DraftTemplateOutlineRow[] {
  return [createSectionRow()];
}

export function outlineHasAnalytics(rows: DraftTemplateOutlineRow[]): boolean {
  return rows.some(
    (row) =>
      isAnalyticsRow(row) || row.children.some((child) => isAnalyticsRow(child))
  );
}

export function usedSectionTemplateIds(
  rows: DraftTemplateOutlineRow[]
): Set<string> {
  const ids = new Set<string>();
  for (const row of rows) {
    if (row.sectionTemplateId) ids.add(row.sectionTemplateId);
    for (const child of row.children) {
      if (child.sectionTemplateId) ids.add(child.sectionTemplateId);
    }
  }
  return ids;
}

function isKeptSection(row: DraftTemplateOutlineRow): boolean {
  if (row.role === 'category') return false;
  if (row.sectionTemplateId) return true;
  return row.title.trim().length > 0;
}

function normalizeSection(
  row: DraftTemplateOutlineRow
): DraftTemplateOutlineRow {
  return {
    ...row,
    role: 'section',
    title: isAnalyticsRow(row) ? 'Analytics' : row.title.trim() || row.title,
    children: [],
  };
}

/**
 * Drop blank custom section titles. Drop categories that have no sections after
 * trim.
 */
export function trimOutline(
  rows: DraftTemplateOutlineRow[]
): DraftTemplateOutlineRow[] {
  const trimmed: DraftTemplateOutlineRow[] = [];

  for (const row of rows) {
    if (row.role === 'category') {
      const children = row.children.filter(isKeptSection).map(normalizeSection);
      if (children.length === 0 && !isAnalyticsRow(row)) continue;
      trimmed.push({
        ...row,
        role: 'category',
        title: isAnalyticsRow(row)
          ? 'Analytics'
          : row.title.trim() || row.title,
        children,
      });
      continue;
    }
    if (!isKeptSection(row)) continue;
    trimmed.push(normalizeSection(row));
  }

  return trimmed;
}

function outlineHasSection(rows: DraftTemplateOutlineRow[]): boolean {
  return rows.some(
    (row) =>
      isAnalyticsRow(row) ||
      row.role === 'section' ||
      row.children.some(
        (child) => child.role === 'section' || isAnalyticsRow(child)
      )
  );
}

export function canSubmitOutline(rows: DraftTemplateOutlineRow[]): boolean {
  return outlineHasSection(trimOutline(rows));
}

export function addCategory(
  rows: DraftTemplateOutlineRow[]
): DraftTemplateOutlineRow[] {
  return [...rows, createCategoryRow()];
}

/** Add a section. If parentId is a category, nest under it; otherwise top-level. */
export function addSection(
  rows: DraftTemplateOutlineRow[],
  parentId: string | null = null
): DraftTemplateOutlineRow[] {
  const nextRow = createSectionRow();
  if (parentId == null) return [...rows, nextRow];
  return rows.map((row) =>
    row.id === parentId && canHoldChildren(row)
      ? { ...row, children: [...row.children, nextRow] }
      : row
  );
}

export function addFromLibrary(
  rows: DraftTemplateOutlineRow[],
  item: SectionLibraryItem,
  parentId: string | null
): DraftTemplateOutlineRow[] {
  if (usedSectionTemplateIds(rows).has(item.id)) return rows;
  const nextRow = createLibraryRow(item);

  // Analytics is its own category, so it is never nested under another category.
  if (parentId == null || isAnalyticsRow(nextRow)) {
    return [...rows, nextRow];
  }

  return rows.map((row) => {
    if (row.id !== parentId || !canHoldChildren(row)) return row;
    return { ...row, children: [...row.children, nextRow] };
  });
}

export function addAnalytics(
  rows: DraftTemplateOutlineRow[],
  parentId: string | null
): DraftTemplateOutlineRow[] {
  return addFromLibrary(
    rows,
    {
      id: OPORD_ANALYTICS_SECTION_TEMPLATE_ID,
      name: 'Analytics',
      type: 'automated',
    },
    parentId
  );
}

export function removeOutlineRow(
  rows: DraftTemplateOutlineRow[],
  rowId: string
): DraftTemplateOutlineRow[] {
  const withoutTop = rows.filter((row) => row.id !== rowId);
  if (withoutTop.length !== rows.length) {
    return withoutTop.length > 0 ? withoutTop : createEmptyOutline();
  }
  return rows.map((row) => ({
    ...row,
    children: row.children.filter((child) => child.id !== rowId),
  }));
}

export function updateOutlineTitle(
  rows: DraftTemplateOutlineRow[],
  rowId: string,
  title: string
): DraftTemplateOutlineRow[] {
  return rows.map((row) => {
    if (row.id === rowId && !row.sectionTemplateId) {
      return { ...row, title };
    }
    return {
      ...row,
      children: row.children.map((child) =>
        child.id === rowId && !child.sectionTemplateId
          ? { ...child, title }
          : child
      ),
    };
  });
}

/** Local row ids for custom titled rows that need a new manual section template. */
export function titledOutlineRowIds(rows: DraftTemplateOutlineRow[]): string[] {
  const ids: string[] = [];
  for (const row of trimOutline(rows)) {
    if (!row.sectionTemplateId) ids.push(row.id);
    for (const child of row.children) {
      if (!child.sectionTemplateId) ids.push(child.id);
    }
  }
  return ids;
}

function sectionIdForRow(
  row: DraftTemplateOutlineRow,
  titleIdToSectionId: Record<string, string>
): string {
  if (row.sectionTemplateId) return row.sectionTemplateId;
  const sectionId = titleIdToSectionId[row.id];
  if (!sectionId) {
    throw new Error(`Missing section template id for outline row ${row.id}`);
  }
  return sectionId;
}

export function buildNestedTemplateSections(
  rows: DraftTemplateOutlineRow[],
  titleIdToSectionId: Record<string, string>
): NestedTemplateSectionLink[] {
  return trimOutline(rows).map((row, index) => {
    const children = row.children.map((child, childIndex) => ({
      section_id: sectionIdForRow(child, titleIdToSectionId),
      order: childIndex + 1,
    }));
    return {
      section_id: sectionIdForRow(row, titleIdToSectionId),
      order: index + 1,
      ...(children.length > 0 ? { children } : {}),
    };
  });
}

export function cloneOutlineFromTemplate(
  sections: TemplateSectionLinkInput[],
  nameBySectionId: Record<string, string>
): DraftTemplateOutlineRow[] {
  const rows = sections.map((section) => {
    const children = (section.children ?? []).map((child) => ({
      id: nextOutlineRowId(),
      title: nameBySectionId[child.section_id] ?? child.section_id,
      role: 'section' as const,
      sectionTemplateId: child.section_id,
      children: [] as DraftTemplateOutlineRow[],
    }));
    return {
      id: nextOutlineRowId(),
      title: nameBySectionId[section.section_id] ?? section.section_id,
      role: (children.length > 0 ? 'category' : 'section') as DraftOutlineRole,
      sectionTemplateId: section.section_id,
      children,
    };
  });
  return rows.length > 0 ? rows : createEmptyOutline();
}

export function cloneTemplateName(sourceName: string): string {
  const base = sourceName.trim() || 'Template';
  return `${base} (copy)`;
}

function findRow(
  rows: DraftTemplateOutlineRow[],
  rowId: string
): {
  row: DraftTemplateOutlineRow;
  parent: DraftTemplateOutlineRow | null;
} | null {
  for (const row of rows) {
    if (row.id === rowId) return { row, parent: null };
    for (const child of row.children) {
      if (child.id === rowId) return { row: child, parent: row };
    }
  }
  return null;
}

/** Reorder a top-level row among top-level siblings. */
export function moveTopLevelRow(
  rows: DraftTemplateOutlineRow[],
  activeId: string,
  overId: string
): DraftTemplateOutlineRow[] {
  const from = rows.findIndex((row) => row.id === activeId);
  const to = rows.findIndex((row) => row.id === overId);
  if (from < 0 || to < 0 || from === to) return rows;
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Reorder a section among its siblings inside a category. */
export function moveSiblingRow(
  rows: DraftTemplateOutlineRow[],
  activeId: string,
  overId: string
): DraftTemplateOutlineRow[] {
  return rows.map((row) => {
    const from = row.children.findIndex((child) => child.id === activeId);
    const to = row.children.findIndex((child) => child.id === overId);
    if (from < 0 || to < 0 || from === to) return row;
    const children = [...row.children];
    const [moved] = children.splice(from, 1);
    children.splice(to, 0, moved);
    return { ...row, children };
  });
}

/**
 * Move a section under a category. No-op if the target cannot hold children or
 * the active row is itself a category.
 */
export function reparentOutlineRow(
  rows: DraftTemplateOutlineRow[],
  childId: string,
  newParentId: string,
  overSiblingId?: string
): DraftTemplateOutlineRow[] {
  const found = findRow(rows, childId);
  const target = rows.find((row) => row.id === newParentId);
  if (!found || !target || !canHoldChildren(target)) return rows;
  if (!isSectionRow(found.row) || found.row.children.length > 0) return rows;
  if (found.parent?.id === newParentId && !overSiblingId) return rows;

  const removed = found.parent
    ? rows.map((row) =>
        row.id === found.parent!.id
          ? {
              ...row,
              children: row.children.filter((child) => child.id !== childId),
            }
          : row
      )
    : rows.filter((row) => row.id !== childId);

  return removed.map((row) => {
    if (row.id !== newParentId) return row;
    const children = [...row.children];
    const overIndex = overSiblingId
      ? children.findIndex((child) => child.id === overSiblingId)
      : -1;
    if (overIndex >= 0) {
      children.splice(overIndex, 0, found.row);
    } else {
      children.push(found.row);
    }
    return { ...row, children };
  });
}

export function applyOutlineDrag(
  rows: DraftTemplateOutlineRow[],
  activeId: string,
  overId: string
): DraftTemplateOutlineRow[] {
  if (activeId === overId) return rows;
  const active = findRow(rows, activeId);
  const over = findRow(rows, overId);
  if (!active || !over) return rows;

  if (!active.parent && !over.parent) {
    if (isSectionRow(active.row) && canHoldChildren(over.row)) {
      return reparentOutlineRow(rows, activeId, over.row.id);
    }
    return moveTopLevelRow(rows, activeId, overId);
  }
  if (active.parent && over.parent && active.parent.id === over.parent.id) {
    return moveSiblingRow(rows, activeId, overId);
  }
  if (active.parent && over.parent && active.parent.id !== over.parent.id) {
    return reparentOutlineRow(rows, activeId, over.parent.id, over.row.id);
  }
  if (active.parent && !over.parent) {
    if (canHoldChildren(over.row)) {
      return reparentOutlineRow(rows, activeId, over.row.id);
    }
    return rows;
  }
  if (!active.parent && over.parent) {
    if (isSectionRow(active.row) && canHoldChildren(over.parent)) {
      return reparentOutlineRow(rows, activeId, over.parent.id, over.row.id);
    }
    return rows;
  }
  return rows;
}

export function mergeSectionLibrary(
  sources: SectionLibraryItem[][]
): SectionLibraryItem[] {
  const byId = new Map<string, SectionLibraryItem>();
  for (const source of sources) {
    for (const item of source) {
      if (!byId.has(item.id)) byId.set(item.id, item);
    }
  }
  return [...byId.values()];
}

export function groupSectionLibrary(items: SectionLibraryItem[]): {
  analytics: SectionLibraryItem | null;
  automated: SectionLibraryItem[];
  ai: SectionLibraryItem[];
  manual: SectionLibraryItem[];
} {
  let analytics: SectionLibraryItem | null = null;
  const automated: SectionLibraryItem[] = [];
  const ai: SectionLibraryItem[] = [];
  const manual: SectionLibraryItem[] = [];

  for (const item of items) {
    if (item.id === OPORD_ANALYTICS_SECTION_TEMPLATE_ID) {
      analytics = { ...item, name: 'Analytics', type: 'automated' };
      continue;
    }
    if (item.type === 'automated') automated.push(item);
    else if (item.type === 'ai') ai.push(item);
    else manual.push(item);
  }

  return { analytics, automated, ai, manual };
}
